import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useCustomer } from '@/features/customers/api';
import { useLocaleFormatters } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useSendQuote } from './api';

const CHANNEL_LABEL_KEYS: Record<'email' | 'whatsapp' | 'sms' | 'link', string> = {
  email: 'newQuote.sendCard.channel.email',
  whatsapp: 'newQuote.sendCard.channel.whatsapp',
  sms: 'newQuote.sendCard.channel.sms',
  link: 'newQuote.sendCard.channel.link',
};

// §33 kill switch, mirroring the server's MESSAGING_ENABLED. Off until
// WrapForce's A2P 10DLC campaign is approved.
const MESSAGING_ENABLED = import.meta.env.VITE_MESSAGING_ENABLED === 'true';

// Selectable minimum lead times (days) for the customer's preferred booking
// date. 0 = no restriction (any date from today onward).
const BOOKING_LEAD_OPTIONS = [0, 3, 7, 14, 21, 28] as const;

function ShareLinkRow({ url, label }: { url: string; label?: string }) {
  const { t } = useTranslation('quotes');
  return (
    <div className="flex items-center gap-2 bg-surface-2 px-3 py-2 text-sm">
      {label ? <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</span> : null}
      <span className="truncate">{url}</span>
      <button
        className="ml-auto shrink-0 font-semibold text-accent-ink"
        onClick={() => {
          navigator.clipboard.writeText(url);
          toast.success(t('newQuote.sendCard.toastLinkCopied'));
        }}
      >
        {t('newQuote.sendCard.copy')}
      </button>
    </div>
  );
}

export function SendQuoteCard({
  workspaceId, customerId, vehicleId, quoteId, sendQuote, totalCents, depositCents,
  bookingLeadDays, setBookingLeadDays, onDone, shareUrl, alreadySent,
}: {
  workspaceId: string | null; customerId: string | null; vehicleId: string | null; quoteId: string | null; sendQuote: ReturnType<typeof useSendQuote>;
  totalCents: number; depositCents: number; bookingLeadDays?: number; setBookingLeadDays?: (days: number) => void; onDone?: () => void;
  shareUrl?: string | null; alreadySent?: boolean;
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const { data: customer } = useCustomer(workspaceId, customerId ?? undefined);
  // Shared-number quote texts are allowed on every paid plan; only trial
  // lacks sharedSmsNotifications.
  const smsAllowed = MESSAGING_ENABLED && !!membership
    && (PLAN_DEFINITIONS[membership.plan].gates.smsMessaging || PLAN_DEFINITIONS[membership.plan].gates.sharedSmsNotifications);
  const hasEmail = !!customer?.email;
  const hasPhone = !!customer?.phone;
  const [channels, setChannels] = useState<string[]>(['email']);
  const [smsConsent, setSmsConsent] = useState(false);
  const [sent, setSent] = useState<{ shareUrl: string; waLink: string | null; smsText: string | null; smsError: string | null } | null>(null);

  function channelDisabledReason(c: string): string | null {
    if (c === 'email' && !hasEmail) return t('newQuote.sendCard.noEmail');
    if ((c === 'whatsapp' || c === 'sms') && !hasPhone) return t('newQuote.sendCard.noPhone');
    if (c === 'sms' && !smsAllowed) return t('newQuote.sendCard.smsLocked');
    return null;
  }

  useEffect(() => {
    setChannels((prev) => {
      const next = prev.filter((c) => {
        if (c === 'email' && !hasEmail) return false;
        if ((c === 'whatsapp' || c === 'sms') && !hasPhone) return false;
        if (c === 'sms' && !smsAllowed) return false;
        return true;
      });
      if (next.length === 0) next.push(hasEmail ? 'email' : 'link');
      return next.length === prev.length && next.every((c, i) => c === prev[i]) ? prev : next;
    });
  }, [smsAllowed, hasEmail, hasPhone]);

  function toggleChannel(c: string) {
    if (channelDisabledReason(c)) return;
    setChannels((prev) => {
      const next = prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c];
      if (c === 'sms' && prev.includes(c)) setSmsConsent(false);
      return next;
    });
  }

  async function handleSend() {
    if (!quoteId) return;
    const result = await sendQuote.mutateAsync({
      channels: channels as ('email' | 'whatsapp' | 'sms' | 'link')[],
      smsConsent: channels.includes('sms') ? smsConsent : undefined,
    });
    setSent(result);
    if (result.smsError) {
      toast.warning(t('newQuote.sendCard.toastSentWithSmsError'), { description: result.smsError });
    } else {
      toast.success(t('newQuote.sendCard.toastSent'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('newQuote.sendCard.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {sent ? (
          <div className="flex flex-col gap-2 border border-success/30 bg-success/10 p-3.5">
            <div className="text-[13px] font-bold text-success">✓ {t('newQuote.sendCard.sentBanner')}</div>
            <ShareLinkRow url={sent.shareUrl} />
            {sent.waLink ? (
              <a href={sent.waLink} target="_blank" rel="noreferrer" className="text-sm font-semibold text-accent-ink">
                {t('newQuote.sendCard.openWhatsapp')}
              </a>
            ) : null}
            {onDone ? (
              <Button onClick={onDone}>{t('newQuote.sendCard.done')}</Button>
            ) : (
              <Button variant="outline" onClick={() => setSent(null)}>{t('newQuote.sendCard.sendAgain')}</Button>
            )}
          </div>
        ) : (
          <>
            {shareUrl ? <ShareLinkRow url={shareUrl} label={t('newQuote.sendCard.currentLink')} /> : null}
            <div className="flex gap-1.5">
              {/* WhatsApp stays hidden until Twilio delivery is wired up; SMS
                  is omitted entirely (not shown disabled) while the shared
                  messaging kill switch is off. */}
              {(MESSAGING_ENABLED ? (['email', 'sms', 'link'] as const) : (['email', 'link'] as const)).map((c) => (
                <button
                  key={c}
                  onClick={() => toggleChannel(c)}
                  disabled={!!channelDisabledReason(c)}
                  title={channelDisabledReason(c) ?? undefined}
                  className={`border px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${channels.includes(c) ? 'border-ink bg-ink text-surface' : 'border-border text-muted'}`}
                >
                  {t(CHANNEL_LABEL_KEYS[c])}
                </button>
              ))}
            </div>
            {channels.includes('sms') && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="quote-sms-consent" className="flex items-start gap-2 text-[11px] text-muted">
                  <input
                    id="quote-sms-consent"
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>{t('newQuote.sendCard.smsConsent')}</span>
                </label>
                <p className="text-[11px] text-muted">
                  {t('newQuote.sendCard.smsPreview', {
                    name: customer?.fullName?.split(' ')[0] ?? '',
                    shop: membership?.name ?? '',
                  })}
                </p>
              </div>
            )}
            {setBookingLeadDays ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bookingLeadDays">{t('newQuote.sendCard.bookingLead.label')}</Label>
                <select
                  id="bookingLeadDays"
                  value={BOOKING_LEAD_OPTIONS.includes((bookingLeadDays ?? 14) as typeof BOOKING_LEAD_OPTIONS[number]) ? (bookingLeadDays ?? 14) : 14}
                  onChange={(e) => setBookingLeadDays(Number(e.target.value))}
                  className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm"
                >
                  {BOOKING_LEAD_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d === 0
                        ? t('newQuote.sendCard.bookingLead.none')
                        : d % 7 === 0
                          ? t('newQuote.sendCard.bookingLead.weeks', { count: d / 7 })
                          : t('newQuote.sendCard.bookingLead.days', { count: d })}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted">{t('newQuote.sendCard.bookingLead.hint')}</p>
              </div>
            ) : null}
            <div className="flex items-center justify-between bg-surface-2 px-3 py-2 text-xs text-muted">
              <span>{t('newQuote.sendCard.total', { amount: formatMoney(totalCents) })}</span>
              <span>{t('newQuote.sendCard.deposit', { amount: formatMoney(depositCents) })}</span>
            </div>
            {!vehicleId && (
              <p className="text-[11px] text-muted">{t('newQuote.sendCard.needVehicle')}</p>
            )}
            <Button
              className="w-full"
              loading={sendQuote.isPending}
              disabled={!quoteId || !vehicleId || channels.length === 0 || (channels.includes('sms') && !smsConsent)}
              onClick={handleSend}
            >
              {alreadySent ? t('newQuote.sendCard.resend') : t('newQuote.sendCard.send')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
